import { useRef, useState, useEffect, useLayoutEffect } from "react";
import { useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const GAP = 24;
const DRIFT = -0.16; // slow "living" drift (px/frame)
const FRICTION = 0.92;
const IDLE_MS = 1400;

function slotWidth(vw) {
  if (vw < 560) return 250;
  if (vw < 1024) return 300;
  return 336;
}

// Interactive, endlessly looping horizontal carousel.
// Drag / wheel / touch to spin with momentum + snap; gentle idle drift; depth focus.
export default function InfiniteCarousel({ items, renderItem }) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const [rendered, setRendered] = useState([]);

  const s = useRef({
    raw: 0,
    v: 0,
    dragging: false,
    lastX: 0,
    lastT: 0,
    moved: 0,
    hover: false,
    snapping: false,
    idle: 0,
    slotInner: 336,
    slotW: 360,
    setW: 360,
  }).current;
  const reduced = useReducedMotion();

  // Build the repeated list sized to always fill the viewport.
  useLayoutEffect(() => {
    const apply = () => {
      const c = containerRef.current;
      if (!c || !items.length) return;
      const vw = c.clientWidth || window.innerWidth;
      const inner = slotWidth(window.innerWidth);
      const slotW = inner + GAP;
      const setW = items.length * slotW;
      const copies = Math.max(3, Math.ceil(2 + vw / setW) + 1);
      s.slotInner = inner;
      s.slotW = slotW;
      s.setW = setW;
      s.raw = 0;
      s.v = 0;
      const list = [];
      for (let cI = 0; cI < copies; cI++)
        for (let i = 0; i < items.length; i++)
          list.push({ p: items[i], idx: i, key: `${cI}-${items[i].id}` });
      slideRefs.current = [];
      setRendered(list);
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
    // eslint-disable-next-line
  }, [items]);

  // Physics + render loop
  useEffect(() => {
    let raf;
    const step = () => {
      raf = requestAnimationFrame(step);
      if (!s.setW) return;
      const now = performance.now();

      if (!s.dragging) {
        if (s.snapping) {
          const target = s.snapTarget != null ? s.snapTarget : Math.round(s.raw / s.slotW) * s.slotW;
          s.raw += (target - s.raw) * 0.16;
          if (Math.abs(target - s.raw) < 0.4) {
            s.raw = target;
            s.snapping = false;
            s.snapTarget = null;
            s.idle = now;
          }
        } else if (Math.abs(s.v) > 0.5) {
          s.raw += s.v;
          s.v *= FRICTION;
          if (Math.abs(s.v) <= 0.5 && !s.hover) s.snapping = true;
        } else if (!reduced && !s.hover && now - s.idle > IDLE_MS) {
          s.raw += DRIFT; // living drift
        }
      }
      applyTransforms();
    };

    const applyTransforms = () => {
      const track = trackRef.current;
      const c = containerRef.current;
      if (!track || !c) return;
      const wrapped = ((s.raw % s.setW) + s.setW) % s.setW;
      const translateX = -wrapped - s.setW;
      track.style.transform = `translate3d(${translateX}px,0,0)`;

      const center = c.clientWidth / 2;
      const arr = slideRefs.current;
      for (let i = 0; i < arr.length; i++) {
        const el = arr[i];
        if (!el) continue;
        const cx = translateX + i * s.slotW + s.slotInner / 2;
        const dist = Math.abs(cx - center);
        const k = Math.max(0, 1 - dist / (c.clientWidth * 0.6));
        const scale = 0.82 + 0.18 * k;
        const op = 0.28 + 0.72 * k;
        el.style.transform = `scale(${scale.toFixed(3)})`;
        el.style.opacity = op.toFixed(3);
      }
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [rendered, reduced]);

  // Wheel (non-passive so we can preventDefault)
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const onWheel = (e) => {
      const d = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!d) return;
      e.preventDefault();
      s.v += d * 0.4;
      s.v = Math.max(-90, Math.min(90, s.v));
      s.snapping = false;
      s.idle = performance.now();
    };
    c.addEventListener("wheel", onWheel, { passive: false });
    return () => c.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line
  }, []);

  const onPointerDown = (e) => {
    s.pdown = true;
    s.dragging = false;
    s.hover = true;
    s.snapping = false;
    s.startX = e.clientX;
    s.lastX = e.clientX;
    s.lastT = performance.now();
    s.v = 0;
    s.moved = 0;
    s.pid = e.pointerId;
  };
  const onPointerMove = (e) => {
    if (!s.pdown) return;
    if (!s.dragging) {
      if (Math.abs(e.clientX - s.startX) <= 5) return;
      s.dragging = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    const dx = e.clientX - s.lastX;
    s.raw -= dx;
    s.moved += Math.abs(dx);
    const now = performance.now();
    const dt = Math.max(1, now - s.lastT);
    s.v = (-dx / dt) * 16;
    s.lastX = e.clientX;
    s.lastT = now;
  };
  const endDrag = (e) => {
    if (!s.pdown) return;
    s.pdown = false;
    if (s.dragging) {
      s.dragging = false;
      e.currentTarget.releasePointerCapture?.(s.pid);
    }
    s.hover = false;
    s.idle = performance.now();
  };
  const onClickCapture = (e) => {
    if (s.moved > 6) {
      e.stopPropagation();
      e.preventDefault();
    }
  };
  const nudge = (dir) => {
    s.snapTarget = Math.round(s.raw / s.slotW) * s.slotW + dir * s.slotW;
    s.snapping = true;
    s.v = 0;
    s.idle = performance.now();
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") nudge(1);
    else if (e.key === "ArrowLeft") nudge(-1);
  };

  return (
    <div
      ref={containerRef}
      data-testid="infinite-carousel"
      className="relative select-none overflow-hidden py-8 outline-none"
      style={{ touchAction: "pan-y", cursor: "grab" }}
      tabIndex={0}
      role="region"
      aria-label="Product carousel — use arrow keys to browse"
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={(e) => {
        endDrag(e);
        s.hover = false;
      }}
      onPointerEnter={() => {
        s.hover = true;
      }}
      onClickCapture={onClickCapture}
    >
      {/* edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-[8%] bg-gradient-to-r from-[#050505] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-[8%] bg-gradient-to-l from-[#050505] to-transparent" />

      <button
        type="button"
        data-testid="carousel-prev"
        onClick={() => nudge(-1)}
        aria-label="Previous product"
        className="absolute left-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#d91616]/50 bg-black/60 text-white/80 backdrop-blur-sm transition-all hover:border-[#d91616] hover:text-white hover:shadow-[0_0_20px_-4px_rgba(217,22,22,0.8)]"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        type="button"
        data-testid="carousel-next"
        onClick={() => nudge(1)}
        aria-label="Next product"
        className="absolute right-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#d91616]/50 bg-black/60 text-white/80 backdrop-blur-sm transition-all hover:border-[#d91616] hover:text-white hover:shadow-[0_0_20px_-4px_rgba(217,22,22,0.8)]"
      >
        <ChevronRight size={18} />
      </button>

      <div ref={trackRef} className="flex" style={{ willChange: "transform" }}>
        {rendered.map((r, i) => (
          <div
            key={r.key}
            ref={(el) => (slideRefs.current[i] = el)}
            className="h-[440px] shrink-0 will-change-transform"
            style={{ width: s.slotInner, marginRight: GAP }}
          >
            {renderItem(r.p, r.idx)}
          </div>
        ))}
      </div>
    </div>
  );
}
